/**
 * OpenWebCamPopUP – Modal to capture image from webcam (exact flow from admin4.0).
 * Props: capturedImg (current captured image data URL or null), closePopup(), addImgCapture(dataUrl).
 */
import React, { useRef, useState, useEffect } from "react";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import Webcam from "react-webcam";
import { ImagePaths } from "../../Utility/ImagePaths";
import "./OpenWebCamPopUP.css";

const closeBtnSrc = ImagePaths?.LabCloseButton?.default ?? ImagePaths?.LabCloseButton;

export default function OpenWebCamPopUP({ capturedImg, closePopup, addImgCapture }) {
  const webcamRef = useRef(null);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [devices, setDevices] = useState([]);
  const [tempCaptureImg, setTempCaptureImg] = useState(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices
      .enumerateDevices()
      .then((deviceList) => {
        const videoDevices = deviceList?.filter((d) => d.kind === "videoinput") ?? [];
        setDevices(videoDevices);
      })
      .catch((err) => console.error("Error enumerating devices:", err));
  }, []);

  const videoConstraints = {
    facingMode: isFrontCamera ? "user" : "environment",
  };

  const handleClose = () => {
    setTempCaptureImg(capturedImg ?? null);
    closePopup?.();
  };

  const handleCapture = () => {
    if (webcamRef.current) {
      const screenshot = webcamRef.current.getScreenshot();
      if (screenshot) setTempCaptureImg(screenshot);
    }
  };

  const handleRetake = () => setTempCaptureImg(null);

  const handleAddImage = () => {
    if (tempCaptureImg) {
      addImgCapture?.(tempCaptureImg);
      closePopup?.();
    }
  };

  const toggleCamera = () => {
    setIsFrontCamera((prev) => !prev);
    setTempCaptureImg(null);
  };

  return (
    <Modal open={true} onClose={handleClose}>
      <Box className="eMed_WebCamPop_Main">
        <Box component="div" className="eMed_WebCam_Header">
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1dvw" }}>Capture Image</Typography>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.25 }} aria-label="Close">
            <Box
              component="img"
              src={typeof closeBtnSrc === "object" && closeBtnSrc?.default ? closeBtnSrc.default : closeBtnSrc}
              alt="close"
              sx={{ width: 18, height: 18, display: "block" }}
            />
          </IconButton>
        </Box>
        <Box component="div" className="eMed_WebCam_Content">
          {tempCaptureImg === null ? (
            <Webcam
              className="eMed_WebCam_Div"
              audio={false}
              mirrored={isFrontCamera}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              width="100%"
              height="100%"
            />
          ) : (
            <img className="eMed_WebCam_Img_Div" src={tempCaptureImg} alt="Captured" />
          )}
        </Box>
        <Box component="div" className="eMed_WebCam_Btn_div">
          <Button
            disabled={tempCaptureImg !== null}
            variant="contained"
            size="small"
            id="eMed_Config_btn"
            onClick={handleCapture}
          >
            Capture
          </Button>
          <Button variant="contained" size="small" id="eMed_Config_btn" onClick={handleRetake}>
            Retake
          </Button>
          <Button
            disabled={tempCaptureImg === null}
            variant="contained"
            size="small"
            id="eMed_Config_btn"
            onClick={handleAddImage}
          >
            Add Image
          </Button>
          {devices?.length > 1 && (
            <Button
              variant="contained"
              size="small"
              id="eMed_Config_btn"
              sx={{ width: "10.5vw", minWidth: 120 }}
              onClick={toggleCamera}
            >
              Switch to {isFrontCamera ? "Back" : "Front"} Camera
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
